export interface ShortcutOptions {
  /** Target to attach the keydown listener on. Defaults to `window`. */
  target?: EventTarget;
  /**
   * Whether to skip shortcuts while the user is typing in an input/textarea/
   * contentEditable element. Defaults to `true` — you almost never want to
   * undo the whole board while the user is just hitting Ctrl+Z inside a text
   * field, where the browser's native text-undo is the correct behavior.
   */
  skipInEditableFields?: boolean;
}

/**
 * Register Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z (Cmd on macOS) to call `onUndo` / `onRedo`.
 * Returns an `unregister` function — always call it on teardown.
 */
export function registerUndoRedoShortcuts(
  onUndo: () => void,
  onRedo: () => void,
  options: ShortcutOptions = {},
): () => void {
  const target = options.target ?? window;
  const skipInEditableFields = options.skipInEditableFields ?? true;

  const handler = (event: Event): void => {
    const e = event as KeyboardEvent;
    if (!isModifierDown(e)) return;
    if (skipInEditableFields && isEditableTarget(e.target)) return;

    const key = e.key.toLowerCase();

    if (key === 'z' && !e.shiftKey) {
      e.preventDefault();
      onUndo();
      return;
    }

    if ((key === 'y') || (key === 'z' && e.shiftKey)) {
      e.preventDefault();
      onRedo();
      return;
    }
  };

  target.addEventListener('keydown', handler);
  return () => target.removeEventListener('keydown', handler);
}

function isModifierDown(e: KeyboardEvent): boolean {
  // Ctrl on Windows/Linux, Cmd on macOS. Both are represented by `ctrlKey` / `metaKey`.
  return e.ctrlKey || e.metaKey;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}
